import { useState, useRef, useEffect } from 'react';
import { Bell, CheckCheck, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from './NotificationsContext';
import { getNotifIcon, getNotifColor } from './notifPresentation';

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return "à l'instant";
  if (m < 60) return `il y a ${m} min`;
  const h = Math.round(m / 60);
  if (h < 24) return `il y a ${h} h`;
  const j = Math.round(h / 24);
  if (j < 7) return `il y a ${j} j`;
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
}

export default function NotificationBell() {
  const { notifications, unreadCount, markAsRead, markAllAsRead, deleteNotification } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const recent = notifications.slice(0, 8);

  const onClickNotif = async (n: typeof notifications[0]) => {
    if (!n.lu) await markAsRead(n.id);
    if (n.lien) {
      setOpen(false);
      navigate(n.lien);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        title="Notifications"
        className="relative w-9 h-9 rounded-lg bg-slate-800/80 hover:bg-slate-800 flex items-center justify-center text-slate-200 transition-colors"
      >
        <Bell size={16} strokeWidth={1.5} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full bg-red-500 text-white flex items-center justify-center border-2 border-slate-950">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[380px] max-w-[calc(100vw-2rem)] bg-slate-900 border border-slate-800 rounded-xl shadow-2xl z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
            <h3 className="font-heading text-sm text-slate-100">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-[11px] text-amber-400 hover:text-amber-300 inline-flex items-center gap-1"
              >
                <CheckCheck size={12} strokeWidth={1.5} /> Tout marquer lu
              </button>
            )}
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {recent.length === 0 && (
              <div className="px-4 py-8 text-center text-xs text-slate-500">
                Aucune notification.
              </div>
            )}
            {recent.map((n) => {
              const Icon = getNotifIcon(n);
              const colorCls = getNotifColor(n, 'soft');
              return (
                <div
                  key={n.id}
                  className={`group relative px-3 py-3 border-b border-slate-800/60 hover:bg-slate-800/40 cursor-pointer ${!n.lu ? 'bg-slate-800/20' : ''}`}
                  onClick={() => onClickNotif(n)}
                >
                  <div className="flex gap-3">
                    <div className={`shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${colorCls}`}>
                      <Icon size={14} strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-xs ${!n.lu ? 'text-slate-100 font-medium' : 'text-slate-300'}`}>{n.titre}</p>
                        {!n.lu && <span className="shrink-0 w-1.5 h-1.5 rounded-full bg-amber-400 mt-1" />}
                      </div>
                      {n.message && <p className="text-[11px] text-slate-400 mt-0.5 line-clamp-2">{n.message}</p>}
                      <p className="text-[10px] text-slate-500 mt-1">{timeAgo(n.created_at)}</p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteNotification(n.id); }}
                      className="opacity-0 group-hover:opacity-100 shrink-0 w-6 h-6 rounded hover:bg-slate-700 text-slate-500 hover:text-slate-300 flex items-center justify-center"
                      title="Supprimer"
                    >
                      <X size={12} strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="px-3 py-2 border-t border-slate-800 bg-slate-900/60">
            <button
              onClick={() => { setOpen(false); navigate('/crm-pro-evenementiel/notifications'); }}
              className="w-full text-center text-xs text-amber-400 hover:text-amber-300 py-1"
            >
              Voir toutes les notifications →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
